/**
 * Challenge Create Sheet — accordion form for creating challenges.
 * Responsive: bottom sheet on mobile, centered modal on desktop.
 * Pattern: same as HabitFormSheet.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Info, Clock, Users, ShieldCheck, Trophy, EyeOff,
  ChevronDown, Plus, Trash2,
} from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useChallengeStore } from '@turnifly/shared/stores'
import { useCreateChallenge, useUpdateChallenge } from '@turnifly/shared/hooks'
import { useChallengeCategories } from '@turnifly/shared/hooks'
import { useMediaUpload } from '@turnifly/shared/hooks'
import { Button } from '@turnifly/shared/ui'

type OpenSection = 'info' | 'time' | 'participants' | 'conditions' | 'prizes' | 'privacy' | null

export const ChallengeCreateSheet: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user } = useAuthStore()
  const { isCreateOpen, selectedType, editingChallenge, goalContext, reset } = useChallengeStore()
  const createChallenge = useCreateChallenge()
  const updateChallenge = useUpdateChallenge()
  const isEditing = !!editingChallenge
  const { data: categories } = useChallengeCategories()
  const { upload, isUploading } = useMediaUpload()

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [coverEmoji, setCoverEmoji] = useState('🎯')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [linkTitle, setLinkTitle] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [startDate, setStartDate] = useState('')
  const [durationDays, setDurationDays] = useState(30)
  const [genderFilter, setGenderFilter] = useState<'all' | 'female' | 'male'>('all')
  const [maxParticipants, setMaxParticipants] = useState<string>('')
  const [isLimited, setIsLimited] = useState(false)
  const [creatorParticipates, setCreatorParticipates] = useState(true)
  const [isPaid, setIsPaid] = useState(false)
  const [price, setPrice] = useState('')
  const [followCreator, setFollowCreator] = useState(true)
  const [likeRequired, setLikeRequired] = useState(true)
  const [commentRequired, setCommentRequired] = useState(true)
  const [shareRequired, setShareRequired] = useState(true)
  const [repostRequired, setRepostRequired] = useState(true)
  const [isHidden, setIsHidden] = useState(false)
  const [prizes, setPrizes] = useState<{ placeFrom: number; placeTo: number; prizeType: string; prizeValue: string }[]>([])
  const [openSection, setOpenSection] = useState<OpenSection>('info')
  const [error, setError] = useState('')

  // Pre-populate form when editing
  useEffect(() => {
    if (!editingChallenge) return
    const ch = editingChallenge as Record<string, unknown>
    setName((ch.name as string) ?? '')
    setDescription((ch.description as string) ?? '')
    setCategoryId((ch.category_id as string) ?? '')
    setCoverEmoji((ch.cover_emoji as string) ?? '🎯')
    setCoverImageUrl((ch.cover_image_url as string) ?? '')
    setLinkTitle((ch.link_title as string) ?? '')
    setLinkUrl((ch.link_url as string) ?? '')
    setTags((ch.tags as string[]) ?? [])
    setStartDate((ch.start_date as string) ?? '')
    setDurationDays((ch.duration_days as number) ?? 30)
    setGenderFilter(((ch.gender_filter as string) ?? 'all') as 'all' | 'female' | 'male')
    setMaxParticipants(ch.max_participants ? String(ch.max_participants) : '')
    setIsLimited(!!ch.max_participants)
    setCreatorParticipates((ch.creator_participates as boolean) ?? true)
    setIsPaid((ch.is_paid as boolean) ?? false)
    setPrice(ch.price ? String(ch.price) : '')
    setIsHidden((ch.is_hidden as boolean) ?? false)
  }, [editingChallenge])

  // Pre-populate from goal context
  useEffect(() => {
    if (!goalContext || editingChallenge) return
    setName(`30 kunlik ${goalContext.goalName}`)
    setDurationDays(30)
  }, [goalContext, editingChallenge])

  const isCompetition = selectedType === 'competition'
  const isPrivate = selectedType === 'private'

  const minDate = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 3)
    return d.toISOString().split('T')[0]
  })()

  const resetForm = useCallback((): void => {
    setName(''); setDescription(''); setCategoryId(''); setCoverEmoji('🎯')
    setCoverImageUrl(''); setLinkTitle(''); setLinkUrl(''); setTags([]); setTagInput('')
    setStartDate(''); setDurationDays(30); setGenderFilter('all'); setMaxParticipants('')
    setIsLimited(false); setCreatorParticipates(true); setIsPaid(false); setPrice('')
    setFollowCreator(true); setLikeRequired(true); setCommentRequired(true)
    setShareRequired(true); setRepostRequired(true); setIsHidden(false)
    setPrizes([]); setOpenSection('info'); setError('')
  }, [])

  const handleClose = useCallback((): void => {
    resetForm()
    reset()
  }, [resetForm, reset])

  const handleCoverUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    if (!file) return
    const result = await upload(file, 'cover')
    if (result) setCoverImageUrl(result.url)
  }, [upload])

  const addTag = useCallback((): void => {
    const tag = tagInput.trim().replace(/^#/, '')
    if (tag && tags.length < 10 && !tags.includes(tag)) {
      setTags([...tags, tag])
      setTagInput('')
    }
  }, [tagInput, tags])

  const addPrize = useCallback((): void => {
    const lastTo = prizes.length > 0 ? prizes[prizes.length - 1].placeTo : 0
    setPrizes([...prizes, { placeFrom: lastTo + 1, placeTo: lastTo + 1, prizeType: 'custom', prizeValue: '' }])
  }, [prizes])

  const handleSubmit = useCallback((): void => {
    setError('')
    if (!name.trim()) { setError(t('challenges.errors.nameRequired')); return }
    if (!startDate) { setError(t('challenges.errors.dateRequired')); return }
    if (!user?.id) return

    const formData = {
      name: name.trim(),
      description: description.trim() || null,
      category_id: categoryId || null,
      cover_image_url: coverImageUrl || null,
      cover_emoji: coverEmoji || null,
      link_title: linkTitle.trim() || null,
      link_url: linkUrl.trim() || null,
      tags,
      start_date: startDate || null,
      duration_days: durationDays,
      gender_filter: genderFilter,
      max_participants: isLimited && maxParticipants ? parseInt(maxParticipants, 10) : null,
      creator_participates: isPrivate ? true : creatorParticipates,
      is_paid: !isPrivate && !isCompetition ? isPaid : false,
      price: isPaid ? parseInt(price, 10) : null,
      is_hidden: isPrivate ? isHidden : false,
    }

    if (isEditing && editingChallenge) {
      updateChallenge.mutate(
        { challengeId: (editingChallenge as { id: string }).id, updates: formData },
        { onSuccess: handleClose },
      )
      return
    }

    createChallenge.mutate(
      {
        creatorId: user.id,
        type: selectedType!,
        timeModel: 'fixed',
        name: name.trim(),
        description: description.trim() || undefined,
        categoryId: categoryId || undefined,
        coverImageUrl: coverImageUrl || undefined,
        coverEmoji: coverEmoji || undefined,
        linkTitle: linkTitle.trim() || undefined,
        linkUrl: linkUrl.trim() || undefined,
        tags,
        startDate,
        durationDays,
        genderFilter,
        maxParticipants: isLimited && maxParticipants ? parseInt(maxParticipants, 10) : null,
        creatorParticipates: isPrivate ? true : creatorParticipates,
        isPaid: !isPrivate && !isCompetition ? isPaid : false,
        price: isPaid ? parseInt(price, 10) : null,
        joinConditions: isPrivate ? {} : {
          follow_creator: followCreator,
          like: likeRequired,
          comment: commentRequired,
          share: shareRequired,
          repost: repostRequired,
        },
        isHidden: isPrivate ? isHidden : false,
        autoJoin: isPrivate || creatorParticipates,
      },
      { onSuccess: handleClose, onError: (err) => setError(err.message) },
    )
  }, [
    name, startDate, user, selectedType, description, categoryId, coverImageUrl, coverEmoji,
    linkTitle, linkUrl, tags, durationDays, genderFilter, isLimited, maxParticipants,
    creatorParticipates, isPrivate, isCompetition, isPaid, price, followCreator,
    likeRequired, commentRequired, shareRequired, repostRequired, isHidden,
    createChallenge, handleClose, t,
  ])

  const toggleSection = (s: OpenSection): void => setOpenSection(openSection === s ? null : s)

  const catList = (categories as Array<{ id: string; name_uz: string; name_en: string; icon: string }>) ?? []
  const isPending = createChallenge.isPending
  const canSubmit = name.trim().length >= 3 && startDate && !isPending

  if (!isCreateOpen || !selectedType) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
        onClick={handleClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[60] lg:bottom-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-surface rounded-t-[28px] lg:rounded-[20px] max-h-[92vh] lg:max-h-[85vh] lg:w-[480px] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
            <button onClick={handleClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/[0.06] text-text-hint tap-highlight-transparent">
              <X size={18} />
            </button>
            <h2 className="text-[15px] font-display font-bold text-text">
              {t(`challenges.create.${selectedType}`)}
            </h2>
            <Button size="sm" onClick={handleSubmit} isLoading={isPending || updateChallenge.isPending} disabled={!canSubmit}>
              {isEditing ? t('common.save') : t('common.create')}
            </Button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
            {/* Cover */}
            <div className="flex items-center gap-3 mb-3">
              <label className="w-16 h-16 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center cursor-pointer hover:bg-white/[0.06] transition-colors shrink-0 overflow-hidden">
                {coverImageUrl ? (
                  <img src={coverImageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl">{coverEmoji}</span>
                )}
                <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
              </label>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-text-hint mb-1">{t('challenges.coverHint')}</p>
                {isUploading && <div className="text-[10px] text-primary">{t('common.uploading')}</div>}
              </div>
            </div>

            {/* 1. Info */}
            <Section icon={Info} title={t('challenges.sections.info')} isOpen={openSection === 'info'} onToggle={() => toggleSection('info')}>
              <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} placeholder={t('challenges.namePlaceholder')} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[14px] text-text placeholder:text-text-hint/30 focus:border-primary/40 focus:outline-none" />
              <div className="flex justify-end text-[10px] text-text-hint/40 -mt-1">{name.length}/80</div>

              <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 300))} placeholder={t('challenges.descPlaceholder')} rows={2} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[13px] text-text placeholder:text-text-hint/30 focus:border-primary/40 focus:outline-none resize-none mt-2" />

              <div className="flex flex-wrap gap-1.5 mt-2">
                {catList.map((cat) => (
                  <button key={cat.id} onClick={() => setCategoryId(cat.id === categoryId ? '' : cat.id)} className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors ${categoryId === cat.id ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-white/[0.04] text-text-hint border border-white/[0.06]'}`}>
                    {cat.icon} {i18n.language === 'en' ? cat.name_en : cat.name_uz}
                  </button>
                ))}
              </div>

              {!isPrivate && (
                <div className="mt-3">
                  <p className="text-[11px] text-text-hint mb-1.5">{t('challenges.tags')} ({tags.length}/10)</p>
                  <div className="flex gap-2">
                    <input value={tagInput} onChange={(e) => setTagInput(e.target.value.slice(0, 30))} onKeyDown={(e) => e.key === 'Enter' && addTag()} placeholder="#tag" className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-text placeholder:text-text-hint/30 focus:border-primary/40 focus:outline-none" />
                    <button onClick={addTag} disabled={!tagInput.trim()} className="px-3 py-2 rounded-lg bg-primary/10 text-primary text-[12px] font-semibold disabled:opacity-30"><Plus size={14} /></button>
                  </div>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-[11px]">
                          #{tag}
                          <button onClick={() => setTags(tags.filter((t) => t !== tag))} className="hover:text-danger">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <input value={linkTitle} onChange={(e) => setLinkTitle(e.target.value.slice(0, 50))} placeholder={t('challenges.linkTitle')} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-[12px] text-text placeholder:text-text-hint/30 focus:border-primary/40 focus:outline-none mt-2" />
              <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://" className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-[12px] text-text placeholder:text-text-hint/30 focus:border-primary/40 focus:outline-none mt-1" />
            </Section>

            {/* 2. Time */}
            <Section icon={Clock} title={t('challenges.sections.time')} isOpen={openSection === 'time'} onToggle={() => toggleSection('time')}>
              <label className="text-[11px] text-text-hint">{t('challenges.startDate')}</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} min={minDate} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[14px] text-text focus:border-primary/40 focus:outline-none" />

              <label className="text-[11px] text-text-hint mt-2">{t('challenges.duration')}</label>
              <div className="flex items-center gap-3">
                <input type="range" min={1} max={30} value={durationDays} onChange={(e) => setDurationDays(parseInt(e.target.value, 10))} className="flex-1 accent-primary" />
                <span className="text-[14px] font-bold text-text w-12 text-right tabular-nums">{durationDays} {t('challenges.days')}</span>
              </div>
            </Section>

            {/* 3. Participants */}
            <Section icon={Users} title={t('challenges.sections.participants')} isOpen={openSection === 'participants'} onToggle={() => toggleSection('participants')}>
              <p className="text-[11px] text-text-hint mb-2">{t('challenges.genderFilter')}</p>
              <div className="flex gap-2">
                {(['all', 'female', 'male'] as const).map((g) => (
                  <button key={g} onClick={() => setGenderFilter(g)} className={`flex-1 py-2.5 rounded-xl text-[12px] font-semibold transition-colors ${genderFilter === g ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-white/[0.04] text-text-hint border border-white/[0.06]'}`}>
                    {t(`challenges.gender.${g}`)}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mt-3">
                <span className="text-[12px] text-text">{t('challenges.limitParticipants')}</span>
                <Switch checked={isLimited} onChange={setIsLimited} />
              </div>
              {isLimited && (
                <input type="number" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} placeholder="100" min={2} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-[13px] text-text focus:border-primary/40 focus:outline-none mt-2" />
              )}

              {!isPrivate && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[12px] text-text">{t('challenges.creatorParticipates')}</span>
                  <Switch checked={creatorParticipates} onChange={setCreatorParticipates} />
                </div>
              )}
            </Section>

            {/* 4. Join Conditions — not for private */}
            {!isPrivate && (
              <Section icon={ShieldCheck} title={t('challenges.sections.conditions')} isOpen={openSection === 'conditions'} onToggle={() => toggleSection('conditions')}>
                {!isCompetition && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[12px] text-text">{t('challenges.isPaid')}</span>
                      <Switch checked={isPaid} onChange={setIsPaid} />
                    </div>
                    {isPaid && (
                      <>
                        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="10 000 — 1 000 000" min={10000} max={1000000} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-[13px] text-text focus:border-primary/40 focus:outline-none" />
                        <p className="text-[10px] text-text-hint/50 mt-1">{t('challenges.commissionNote')}</p>
                      </>
                    )}
                  </>
                )}

                {!isPaid && (
                  <div className="space-y-2 mt-2">
                    <ConditionToggle label={t('challenges.conditions.follow')} checked={followCreator} onChange={setFollowCreator} />
                    <ConditionToggle label={t('challenges.conditions.like')} checked={likeRequired} onChange={setLikeRequired} />
                    <ConditionToggle label={t('challenges.conditions.comment')} checked={commentRequired} onChange={setCommentRequired} />
                    <ConditionToggle label={t('challenges.conditions.share')} checked={shareRequired} onChange={setShareRequired} />
                    <ConditionToggle label={t('challenges.conditions.repost')} checked={repostRequired} onChange={setRepostRequired} />
                  </div>
                )}
              </Section>
            )}

            {/* 5. Prizes — competition only */}
            {isCompetition && (
              <Section icon={Trophy} title={t('challenges.sections.prizes')} isOpen={openSection === 'prizes'} onToggle={() => toggleSection('prizes')}>
                {prizes.map((p, i) => (
                  <div key={i} className="flex gap-2 items-center mb-2">
                    <span className="text-[12px] text-text-hint w-8 shrink-0">{p.placeFrom === p.placeTo ? `#${p.placeFrom}` : `#${p.placeFrom}-${p.placeTo}`}</span>
                    <input value={p.prizeValue} onChange={(e) => { const np = [...prizes]; np[i].prizeValue = e.target.value; setPrizes(np) }} placeholder={t('challenges.prizePlaceholder')} className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-text focus:border-primary/40 focus:outline-none" />
                    <button onClick={() => setPrizes(prizes.filter((_, j) => j !== i))} className="text-danger/50 hover:text-danger"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={addPrize} className="flex items-center gap-1.5 text-primary text-[12px] font-semibold mt-1 tap-highlight-transparent">
                  <Plus size={14} />{t('challenges.addPrize')}
                </button>
              </Section>
            )}

            {/* 6. Privacy — private only */}
            {isPrivate && (
              <Section icon={EyeOff} title={t('challenges.sections.privacy')} isOpen={openSection === 'privacy'} onToggle={() => toggleSection('privacy')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[12px] text-text">{t('challenges.hideFromAll')}</p>
                    <p className="text-[10px] text-text-hint">{t('challenges.hideFromAllDesc')}</p>
                  </div>
                  <Switch checked={isHidden} onChange={setIsHidden} />
                </div>
              </Section>
            )}

            {error && (
              <div className="p-3 rounded-xl bg-danger/10 border border-danger/20">
                <p className="text-[12px] text-danger">{error}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// ── Helpers ─────────────────────────────────────────────────

const Section: React.FC<{
  icon: React.ElementType
  title: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ icon: Icon, title, isOpen, onToggle, children }) => (
  <div className="rounded-xl border border-white/[0.06] overflow-hidden">
    <button onClick={onToggle} className="w-full flex items-center gap-2.5 px-4 py-3 tap-highlight-transparent">
      <Icon size={16} className={isOpen ? 'text-primary' : 'text-text-hint'} />
      <span className="flex-1 text-left text-[13px] font-semibold text-text">{title}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <ChevronDown size={14} className="text-text-hint" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
          <div className="px-4 pb-4 space-y-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

const Switch: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <button onClick={() => onChange(!checked)} className={`w-11 h-6 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-white/[0.1]'}`}>
    <motion.div animate={{ x: checked ? 20 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }} className="w-5 h-5 rounded-full bg-white shadow" />
  </button>
)

const ConditionToggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-[12px] text-text">{label}</span>
    <Switch checked={checked} onChange={onChange} />
  </div>
)
